// Anatomia da tela de Aniversários (/birthday-settings) no padrão Aeros Dark —
// mockup "Tela Aeros Dark - Aniversários".
//
// Os campos de formulário ficam escuros no tema escuro (#0E1119, como o
// mockup) e usam a surface2 no tema claro; o restante segue os tokens.
import { makeStyles, alpha } from "@material-ui/core/styles";
import { AEROS, stateText } from "../../styles/aerosTokens";

export const MONO = "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

const useBirthdayPageStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const borderSoft = isLight ? theme.palette.divider : "rgba(255,255,255,0.06)";
  const borderCard = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  const fieldBg = isLight ? theme.palette.aeros.surface2 : "#0E1119";

  return {
    pageSubtitle: {
      fontSize: 12,
      color: theme.palette.text.secondary,
      lineHeight: 1.3,
      marginTop: 1,
    },
    nextSendPill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 99,
      padding: "5px 12px",
      "& .MuiSvgIcon-root": {
        fontSize: 15,
        color: stateText(theme, AEROS.warning, "warning"),
      },
    },

    // ---- corpo: duas colunas 1.5fr / 1fr ----
    body: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: "24px 12px",
      display: "grid",
      gridTemplateColumns: "minmax(0,1.5fr) minmax(0,1fr)",
      gap: 18,
      alignItems: "start",
      ...theme.scrollbarStylesSoft,
      [theme.breakpoints.down("md")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    column: { display: "flex", flexDirection: "column", gap: 14, minWidth: 0 },

    // ---- card recolhível ----
    card: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 14,
      overflow: "hidden",
    },
    cardHeader: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      padding: "14px 18px",
      borderBottom: `1px solid ${borderSoft}`,
    },
    cardHeaderFlat: { borderBottom: "none" },
    tile: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flex: "none",
      "& .MuiSvgIcon-root": { fontSize: 19 },
    },
    tileCyan: {
      backgroundColor: alpha(AEROS.cyan, 0.12),
      color: stateText(theme, AEROS.cyan, "info"),
    },
    tilePurple: {
      backgroundColor: alpha(theme.palette.primary.main, 0.14),
      color: stateText(theme, "#C08BFF", "purple"),
    },
    cardTitle: {
      fontSize: 13.5,
      fontWeight: 700,
      color: theme.palette.text.primary,
      lineHeight: 1.3,
    },
    cardSubtitle: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      lineHeight: 1.35,
    },
    toggleGroup: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      fontSize: 12,
      fontWeight: 600,
      flex: "none",
    },
    toggleOn: { color: stateText(theme, AEROS.success, "success") },
    toggleOff: { color: theme.palette.aeros.textTertiary },
    expandButton: {
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "&:hover": { color: theme.palette.text.primary, backgroundColor: "transparent" },
      "& .MuiSvgIcon-root": { fontSize: 20 },
    },
    cardBody: {
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 14,
    },

    // ---- switch no formato do mockup (36x20) ----
    switchRoot: {
      width: 36,
      height: 20,
      padding: 0,
      display: "flex",
      overflow: "visible",
      flex: "none",
      "& .MuiSwitch-switchBase": {
        padding: 2,
        color: theme.palette.aeros.textTertiary,
        "&.Mui-checked": {
          transform: "translateX(16px)",
          color: "#07080D",
          "& + .MuiSwitch-track": {
            opacity: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${AEROS.cyan})`,
          },
        },
        "&.Mui-disabled + .MuiSwitch-track": { opacity: 0.4 },
      },
      "& .MuiSwitch-thumb": { width: 16, height: 16, boxShadow: "none" },
      "& .MuiSwitch-track": {
        borderRadius: 99,
        opacity: 1,
        backgroundColor: isLight
          ? theme.palette.aeros.borderStrong
          : "rgba(255,255,255,0.12)",
      },
    },

    // ---- campos ----
    fieldRow: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1fr) minmax(0,1.4fr)",
      gap: 12,
      [theme.breakpoints.down("xs")]: { gridTemplateColumns: "minmax(0,1fr)" },
    },
    fieldLabel: {
      fontSize: 12,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      marginBottom: 6,
    },
    helper: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      marginTop: 5,
    },
    input: {
      "& .MuiOutlinedInput-root": {
        backgroundColor: fieldBg,
        borderRadius: 10,
        fontSize: 13,
        color: theme.palette.text.primary,
        "& fieldset": { borderColor: borderInput },
        "&:hover fieldset": { borderColor: alpha(AEROS.cyan, 0.5) },
        "&.Mui-focused fieldset": { borderColor: AEROS.cyan, borderWidth: 1 },
        "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(AEROS.cyan, 0.15)}` },
      },
      "& .MuiOutlinedInput-input": { padding: "9px 12px" },
      "& .MuiSelect-select": { padding: "9px 12px" },
    },
    inputTime: {
      "& .MuiOutlinedInput-input": {
        fontSize: 13.5,
        fontVariantNumeric: "tabular-nums",
      },
    },
    // Sem `minHeight` aqui de propósito: o multiline do MUI v4 mede a altura
    // por um textarea sombra e escreve `height` inline — um min-height no
    // mesmo elemento inflava o campo para centenas de pixels. A altura vem de
    // `minRows`, que é o que o TextareaAutosize entende.
    inputMultiline: {
      "& .MuiOutlinedInput-root": { padding: 0 },
      "& .MuiOutlinedInput-inputMultiline": {
        padding: "10px 12px",
        fontSize: 13,
        lineHeight: 1.6,
      },
    },
    connOption: { display: "flex", alignItems: "center", gap: 8, minWidth: 0 },
    connDot: { width: 8, height: 8, borderRadius: "50%", flex: "none" },
    connTag: {
      fontSize: 10,
      fontWeight: 700,
      borderRadius: 5,
      padding: "1px 6px",
    },
    connTagOn: {
      color: stateText(theme, AEROS.success, "success"),
      backgroundColor: alpha(AEROS.success, 0.12),
    },
    connTagOff: {
      color: stateText(theme, AEROS.error, "error"),
      backgroundColor: alpha(AEROS.error, 0.12),
    },

    // ---- chips de variável ----
    messageLabelRow: { display: "flex", alignItems: "center", marginBottom: 6 },
    varChips: { marginLeft: "auto", display: "flex", gap: 6 },
    varChip: {
      fontFamily: MONO,
      fontSize: 11,
      fontWeight: 600,
      textTransform: "none",
      minWidth: 0,
      color: stateText(theme, "#C08BFF", "purple"),
      backgroundColor: alpha(theme.palette.primary.main, 0.14),
      borderRadius: 6,
      padding: "2px 8px",
      lineHeight: 1.5,
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.24) },
    },

    // ---- linha de status + botão de teste ----
    statusRow: { display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" },
    sentToday: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      fontSize: 12,
      color: theme.palette.text.secondary,
      "& .MuiSvgIcon-root": {
        fontSize: 15,
        color: stateText(theme, AEROS.success, "success"),
      },
    },
    testButton: {
      marginLeft: "auto",
      border: `1px solid ${alpha(theme.palette.primary.main, 0.4)}`,
      color: stateText(theme, "#C08BFF", "purple"),
      borderRadius: 8,
      padding: "6px 12px",
      fontSize: 12,
      fontWeight: 600,
      textTransform: "none",
      "& .MuiButton-startIcon .MuiSvgIcon-root": { fontSize: 15 },
      "&:hover": { backgroundColor: alpha(theme.palette.primary.main, 0.08) },
    },

    // ---- linha "criar informativo" ----
    optionRow: {
      display: "flex",
      alignItems: "center",
      gap: 12,
      border: `1px solid ${isLight ? theme.palette.divider : "rgba(255,255,255,0.08)"}`,
      borderRadius: 12,
      padding: "10px 14px",
      "& > .MuiSvgIcon-root": {
        fontSize: 18,
        color: stateText(theme, "#C08BFF", "purple"),
        flex: "none",
      },
    },
    optionTitle: { fontSize: 13, fontWeight: 600, color: theme.palette.text.primary },
    optionDesc: { fontSize: 11.5, color: theme.palette.text.secondary, lineHeight: 1.35 },

    // ---- coluna direita ----
    sideCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 14,
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: 12,
    },
    eyebrow: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    eyebrowRow: { display: "flex", alignItems: "center" },
    linkButton: {
      marginLeft: "auto",
      fontSize: 11.5,
      fontWeight: 600,
      textTransform: "none",
      minWidth: 0,
      padding: 0,
      color: stateText(theme, AEROS.cyan, "info"),
      "&:hover": { backgroundColor: "transparent", textDecoration: "underline" },
    },

    // ---- pré-visualização no WhatsApp ----
    chatFrame: {
      backgroundColor: fieldBg,
      border: `1px solid ${borderSoft}`,
      borderRadius: 12,
      padding: 14,
      display: "flex",
      flexDirection: "column",
      gap: 8,
    },
    chatHead: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      paddingBottom: 8,
      borderBottom: `1px solid ${borderSoft}`,
    },
    chatAvatar: {
      width: 28,
      height: 28,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#07080D",
      flex: "none",
    },
    chatName: { fontSize: 12.5, fontWeight: 600, color: theme.palette.text.primary },
    chatMeta: {
      marginLeft: "auto",
      fontSize: 10.5,
      color: theme.palette.aeros.textTertiary,
    },
    bubble: {
      alignSelf: "flex-end",
      maxWidth: "92%",
      backgroundColor: alpha(AEROS.success, 0.16),
      border: `1px solid ${alpha(AEROS.success, 0.3)}`,
      borderRadius: "14px 14px 4px 14px",
      padding: "9px 12px",
      fontSize: 12.5,
      lineHeight: 1.55,
      color: theme.palette.text.primary,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    },
    bubbleTime: {
      textAlign: "right",
      fontSize: 10,
      color: theme.palette.aeros.textTertiary,
      marginTop: 4,
    },
    bubbleEmpty: {
      alignSelf: "center",
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      padding: "12px 0",
    },
    note: {
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "flex-start",
      gap: 5,
      lineHeight: 1.5,
      "& .MuiSvgIcon-root": { fontSize: 13, marginTop: 2, flex: "none" },
    },

    // ---- próximos aniversários ----
    upcomingRow: { display: "flex", alignItems: "center", gap: 10 },
    upcomingDate: { width: 40, textAlign: "center", flex: "none" },
    upcomingDay: {
      fontSize: 15,
      fontWeight: 700,
      lineHeight: 1,
      fontVariantNumeric: "tabular-nums",
      color: theme.palette.text.primary,
    },
    upcomingDayToday: { color: stateText(theme, AEROS.cyan, "info") },
    upcomingMonth: {
      fontSize: 9.5,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
    },
    upcomingAvatar: {
      width: 32,
      height: 32,
      borderRadius: "50%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 700,
      color: "#07080D",
      flex: "none",
    },
    upcomingName: {
      fontSize: 12.5,
      fontWeight: 600,
      color: theme.palette.text.primary,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    upcomingMeta: {
      fontSize: 11,
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: 4,
      "& .MuiSvgIcon-root": { fontSize: 12 },
    },
    upcomingStatus: {
      fontSize: 11,
      fontWeight: 600,
      borderRadius: 99,
      padding: "2px 8px",
      flex: "none",
    },
    emptyLine: {
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      padding: "6px 0",
    },

    // ---- KPIs ----
    kpiGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(3,minmax(0,1fr))",
      gap: 10,
    },
    kpiCard: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderCard}`,
      borderRadius: 12,
      padding: "12px 14px",
    },
    kpiValue: {
      fontSize: 18,
      fontWeight: 700,
      letterSpacing: "-0.02em",
      fontVariantNumeric: "tabular-nums",
      lineHeight: 1.1,
      color: theme.palette.text.primary,
    },
    kpiLabel: { fontSize: 11, color: theme.palette.text.secondary, marginTop: 3 },

    loading: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 10,
      color: theme.palette.text.secondary,
      fontSize: 13,
    },
  };
});

export default useBirthdayPageStyles;
